import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { DialogPromptConversationReadWithBlankSpeak, DialogPromptConversationRelated, IDialogPromptSentence, SPEAKER_NAME_A } from '../../api/DialogPromptApi';
import AddSelfStudyBlueIcon from '../../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import RightArrowBlueIcon from '../../asset/icon-right-arrow-blue.png';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';
import { useKaiTiFontStyles } from '../../font/KaiTiFont';
import ChatBubble from '../ChatBubble';


import { useHistory, useLocation } from 'react-router-dom';
import { useAudioRecorder } from '../../AudioRecorder';
import { sentenceScoreAllGood, sentenceScoreNonSilentFailed, sentenceScoreAnyFailed } from '../../Score';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptConversationApi, IDialogPromptConversation } from '../../api/DialogPromptConversationApi';
import { ISentenceScore, ScoreApi } from '../../api/ScoreApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import AddSentenceDialog from '../AddSentenceDialog';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getConversationSpeakerRole } from '../TeacherAudioReportButton';
import { SentenceScoreMediumResult, SentenceScoreTryAgain, SentenceScoreTryAgainNo, SentenceScoreTryAgainYes, getAudioByText } from './DialogPromptComponent';
import { dialogPromptClassNameContainsProp } from './DialogPromptForm';

interface DialogPromptConversationRelatedComponentProps {
    dialogPrompt: DialogPromptConversationRelated;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const isSentenceScoreChatObject = (chatObject: ISentenceScore | IDialogPromptSentence | undefined): chatObject is ISentenceScore => {
    return !!chatObject && 'score' in chatObject && 'pinyin_list_spoken' in chatObject && 'is_silence' in chatObject;
};

const isNonSilentFailedSentenceScore = (chatObject: ISentenceScore | IDialogPromptSentence | undefined): chatObject is ISentenceScore => {
    return isSentenceScoreChatObject(chatObject) && sentenceScoreNonSilentFailed(chatObject);
};

const getReadWithBlankSentenceText = (
    dialogPrompt: DialogPromptConversationReadWithBlankSpeak,
    chatObject: IDialogPromptSentence,
    sentenceIndex: number,
    fallbackText: string
) => {
    const promptSentence = dialogPrompt.dialog_prompt_sentence_list?.[sentenceIndex]
        ?? dialogPrompt.dialog_prompt_sentence_list?.find((sentence) => sentence.order_index === chatObject.order_index);
    const sentenceTextAlternate = promptSentence?.sentence_text_alternate;
    return typeof sentenceTextAlternate === 'string'
        ? sentenceTextAlternate.replace(/\*/g, '__')
        : fallbackText;
};

const DialogPromptConversationRelatedComponent: React.FC<DialogPromptConversationRelatedComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, }) => {

    // console.log('Original Type of dialogPrompt:', dialogPrompt.constructor.name);

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setLeftNavigationList,
        setRightNavigationList,
        setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const kaiTiFontClass = useKaiTiFontStyles();

    const [chatBubbleDone, setChatBubbleDone] = React.useState<boolean>(false);


    const dialogPromptSentenceListRef = React.useRef<IDialogPromptSentence[]>([]);
    const [conversationLoaded, setConversationLoaded] = React.useState(false);
    const initialDialogPromptIndex = dialogPrompt.class_name == "DialogPromptConversationSpeakOnlyModel" ? 0 : 0;
    const dialogPromptIndexRef = React.useRef(initialDialogPromptIndex); // Initialize the ref outside the component function

    // SpeakOnly: the student speaks every other line, starting at initialDialogPromptIndex and
    // stepping by 2. Those same lines are the ones blanked out - the student says them from
    // memory - while the partner's lines stay visible as cues. Both the recorded line and the
    // blanked line are derived from this one predicate so they cannot drift apart again.
    const isStudentSpokenLine = (sentenceIndex: number) => sentenceIndex % 2 === initialDialogPromptIndex % 2;
    const [allDialogSentenceDone, setAllDialogSentenceDone] = React.useState(false);

    const [isScoring, setIsScoring] = React.useState(false);

    // contains both conversation sentences, and chats
    const [chatObjectList, setChatObjectList] = React.useState<(ISentenceScore | IDialogPromptSentence)[]>([]);

    // flags that are set in this order. should be same length as chat object list
    const [chatBubbleSentenceScoreDone, setChatBubbleSentenceScoreDone] = React.useState<boolean[]>([false]);
    const [chatBubbleTeacherResponseDone, setChatBubbleTeacherResponseDone] = React.useState<boolean[]>([false]);
    const [tryAgainClickedList, setTryAgainClickedList] = React.useState<(boolean | null)[]>([]);
    const [chatBubbleTryAgainDone, setChatBubbleTryAgainDone] = React.useState<boolean[]>([false]);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState<boolean[]>([false]);

    // chat bubble audio
    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreMediumResultAudio, setSentenceScoreMediumResultAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreTryAgainAudio, setSentenceScoreTryAgainAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainYesAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainNoAudio] = React.useState<Blob | null | undefined>(undefined);


    React.useEffect(() => {
        console.log("[] getting audio, getting conversation")

        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreMediumResult, setSentenceScoreMediumResultAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgain, setSentenceScoreTryAgainAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainYes, setSentenceScoreTryAgainYesAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainNo, setSentenceScoreTryAgainNoAudio)

        DialogPromptConversationApi.getById(dialogPrompt.dialog_prompt_conversation_id)
            .then((response) => {
                const oldDialogPrompt = response.data as IDialogPromptConversation;
                dialogPromptSentenceListRef.current = oldDialogPrompt.dialog_prompt_sentence_list;
                setConversationLoaded(true);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [clearFooterList, dialogPrompt.dialog_prompt_conversation_id, dialogPrompt.no_response, dialogPrompt.prompt, dialogPrompt.yes_response, history, location, setErrorMessage]);


    const onStop = React.useCallback((audioData: Blob) => {
        setIsScoring(true)
        const formData = new FormData();
        formData.append('audio', audioData);

        ScoreApi.getScore(dialogPromptSentenceListRef.current[dialogPromptIndexRef.current].sentence_id, formData)
            .then(response => {
                setIsScoring(false)
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(sentenceScore => {
                console.log("retrieved and setting chat object list")

                UserDialogPromptApi.addSpeakingAttempt(dialogPrompt.id, sentenceScore.score_mean)
                    .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })

                sentenceScore.audioData = audioData;
                setChatObjectList((previous) => [...previous, sentenceScore]);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [dialogPrompt.id, setErrorMessage, history, location]);

    const { startRecording, stopRecording, isRecording } = useAudioRecorder({ setErrorMessage, onStop });

    const [shouldJiggleSkipArrow, setShouldJiggleSkipArrow] = React.useState(false);
    const jiggleKeyframes = React.useMemo(() => ({
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '12.5%': { transform: 'rotate(-20deg)' },
            '37.5%': { transform: 'rotate(20deg)' },
            '52.5%': { transform: 'rotate(-20deg)' },
            '77.5%': { transform: 'rotate(20deg)' },
            '100%': { transform: 'rotate(0)' },
        },
    }), []);
    const jiggleDuration = 500
    const jiggleIteration = 8
    React.useEffect(() => {
        console.log("use effect()")
        if (chatObjectList.length === 0) return;

        const lastChatObject = chatObjectList[chatObjectList.length - 1];
        if (!isNonSilentFailedSentenceScore(lastChatObject)) {
            setShouldJiggleSkipArrow(false);
            return;
        }

        const failedCount = chatObjectList.reduce(
            (acc, chatObject) => acc + (isNonSilentFailedSentenceScore(chatObject) ? 1 : 0),
            0
        );

        if (failedCount % 2 === 0) {
            console.log("jiggling skip arrow")
            setShouldJiggleSkipArrow(true);
            const totalDuration = jiggleDuration * jiggleIteration
            const t = setTimeout(() => setShouldJiggleSkipArrow(false), totalDuration + 50); // length of the animation
            return () => clearTimeout(t);
        } else {
            setShouldJiggleSkipArrow(false);
        }
    }, [chatObjectList]);

    // left nav, skip button
    React.useEffect(() => {
        if (allDialogSentenceDone) {
            setLeftNavigationList([]);
        } else {
            // only allow skip if last chat object is score object
            const lastChatObject = chatObjectList[chatObjectList.length - 1]
            if (isNonSilentFailedSentenceScore(lastChatObject)) {
                setLeftNavigationList([
                    <IconButton
                        onClick={handleCancelClick}
                        sx={{
                            ...jiggleKeyframes,
                            animation: shouldJiggleSkipArrow ? `jiggle ${jiggleDuration}ms ease-in-out ${jiggleIteration}` : 'none',
                        }}
                    >
                        <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                    </IconButton>
                ])
            } else {
                setLeftNavigationList([]);
            }
        }

        if (chatBubbleContainerRef.current) {
        scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [chatObjectList, allDialogSentenceDone, shouldJiggleSkipArrow, chatBubbleContainerRef, jiggleKeyframes, setLeftNavigationList]);

    // right nav, add sentence button
    React.useEffect(() => {
        // update every time chatObject changes
        // cannot trigger on  dialogPromptSentenceList or dialogPromptIndex because they are REF not STATE
        if (allDialogSentenceDone == false && dialogPromptSentenceListRef.current.length > 0 && dialogPromptIndexRef.current >= 0) {
            setRightNavigationList([
                <Tooltip title="Add to Pronunciation" arrow>
                    <IconButton onClick={handleAddSentenceClick} sx={{ alignSelf: 'center' }}>
                        <img src={AddSelfStudyBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                    </IconButton>
                </Tooltip>
            ])
        } else {
            setRightNavigationList([]);
        }
    }, [chatObjectList, allDialogSentenceDone, setRightNavigationList])

    // action list, recording or scoring
    React.useEffect(() => {
        if (allDialogSentenceDone || dialogPrompt.class_name == "DialogPromptConversationListenWithoutCharacterModel") {
            setActionList([]);
        } else {
            setActionList([
                <IconButton aria-label={isRecording ? "Stop recording" : "Record"} onClick={isRecording ? stopRecording : startRecording}>
                    {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                        : isScoring ? <CircularProgress size={24} sx={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                            : <img src={MicrophoneBlueIcon} alt="" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />}
                </IconButton>
            ]);
        }

    }, [allDialogSentenceDone, isRecording, isScoring, chatObjectList, tryAgainClickedList, dialogPrompt.class_name, setActionList, startRecording, stopRecording])

    const updateChatObjectListFromDialogPromptIndex = () => {
        setChatObjectList((previous) => {
            const additionalDialogPrompt: IDialogPromptSentence[] = [];

            for (let i = 0; i <= dialogPromptIndexRef.current; i++) {
                if (i < dialogPromptSentenceListRef.current.length) {
                    const dialogPromptSentence = dialogPromptSentenceListRef.current[i];

                    const isNotInChatObjectList = !previous.some(
                        (chatObject) => 'dialog_prompt_id' in chatObject && 'sentence_id' in chatObject && chatObject.id === dialogPromptSentence.id);

                    if (isNotInChatObjectList) {
                        additionalDialogPrompt.push(dialogPromptSentence);
                    }
                }
            }

            return additionalDialogPrompt.length > 0
                ? [...previous, ...additionalDialogPrompt]
                : previous;
        });
    }

    React.useEffect(() => {
        // Show the conversation as soon as it loads instead of waiting for the teacher bubble to
        // report done. ChatBubble paces its typing animation off the prompt's audio duration, so a
        // prompt with no TTS yet never reports done - which left the first line's card unpushed
        // until after the student had already recorded it, and then pushed two cards at once.
        // chatBubbleDone stays in the dependency list so the update still runs when the bubble
        // finishes; updateChatObjectListFromDialogPromptIndex is idempotent.
        if (conversationLoaded) {
            updateChatObjectListFromDialogPromptIndex();
        }
    }, [chatBubbleDone, conversationLoaded])

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
        scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [chatObjectList, chatBubbleContainerRef]);

    const handleSpeakerClick = (dialogPromptIndex: (number | null) = null) => {
        let audioPromise;
        if (dialogPromptIndex == null) {
            dialogPromptIndex = dialogPromptIndexRef.current
        }

        // The voice follows the script's speaker_name, not the line's position, so a character
        // keeps the same voice even in a conversation where they do not speak first. Falls back
        // to index parity for rows with no speaker_name, matching
        // buildConversationAudioReportContext below.
        const sentence = dialogPromptSentenceListRef.current[dialogPromptIndex];
        const speakerName = sentence.speaker_name ?? (dialogPromptIndex % 2 === 0 ? 'A' : 'B');
        if (speakerName.trim().toUpperCase() === SPEAKER_NAME_A) {
            audioPromise = AudioApi.getSpeakerA(sentence.sentence_id)
        } else {
            audioPromise = AudioApi.getSpeakerB(sentence.sentence_id)
        }


        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob, {
                    onEnded: () => {
                        if (dialogPrompt.class_name == "DialogPromptConversationListenWithoutCharacterModel") {
                            setChatBubbleContinueDone((previous) => {
                                const idx = chatObjectList.length - 1;
                                if (previous[idx] === true) return previous;
                                const updatedArray = [...previous];
                                updatedArray[idx] = true;
                                return updatedArray
                            });
                        }
                    }
                })
            })
            .catch(err => {
            handleErrorMessage(err, setErrorMessage, history, location);
        })
    };

    const buildConversationAudioReportContext = React.useCallback((sentenceIndex: number) => {
        const sentence = dialogPromptSentenceListRef.current[sentenceIndex];
        if (!sentence) return null;
        const speakerName = sentence.speaker_name ?? (sentenceIndex % 2 === 0 ? 'A' : 'B');
        return {
            sentenceId: sentence.sentence_id,
            dialogPromptId: dialogPrompt.id,
            textToSpeechSpeakerId: getConversationSpeakerRole(speakerName),
            audioText: sentence.sentence_text ?? null,
            audioRole: `conversation_speaker_${speakerName}`,
        };
    }, [dialogPrompt.id]);

    const handleNextWordClick = React.useCallback(() => {
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[chatObjectList.length - 1] = false;
            return updatedArray;
        })
    }, [chatObjectList.length, setTryAgainClickedList]);
    const handleTryAgainClick = React.useCallback(() => {
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[chatObjectList.length - 1] = true;
            return updatedArray;
        })
    }, [chatObjectList.length, setTryAgainClickedList]);

    React.useEffect(() => {
        if (chatBubbleTryAgainDone[chatObjectList.length - 1]) {
            setLeftNavigationList([
                <IconButton onClick={handleCancelClick}>
                    <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
            setActionList([
                <Button variant="contained" onClick={handleNextWordClick} className="secondaryButton" >
                    No, next sentence
                </Button>,
                <Button variant="contained" onClick={handleTryAgainClick} >
                    Yes, Try Again
                </Button>
            ])
        }
    }, [chatBubbleTryAgainDone, chatObjectList.length, handleNextWordClick, handleTryAgainClick, setActionList, setLeftNavigationList]);

    React.useEffect(() => {
        // continue to next sentence
        if (chatBubbleContinueDone[chatObjectList.length - 1]) {
            console.log("[chatBubbleContinueDone] getting next dialog, setting dialogPromptIndexRef.current ", dialogPromptIndexRef.current)
            if (dialogPromptIndexRef.current + 1 >= dialogPromptSentenceListRef.current.length) {
                // no more sentences
                setDialogPromptComplete(dialogPrompt.id)
                setAllDialogSentenceDone(true)
            } else if (dialogPrompt.class_name == "DialogPromptConversationSpeakOnlyModel" && dialogPromptIndexRef.current + 2 < dialogPromptSentenceListRef.current.length) {
                // only speak every other sentence
                dialogPromptIndexRef.current += 2;
                updateChatObjectListFromDialogPromptIndex();
            } else {
                // standard next sentence
                dialogPromptIndexRef.current += 1;
                updateChatObjectListFromDialogPromptIndex();
            }
        }
    }, [chatBubbleContinueDone, chatObjectList.length, dialogPrompt.class_name, dialogPrompt.id, setDialogPromptComplete])

    /**
     * Skip cancel stuff
     */
    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);

    const handleCancelClick = () => {
        setOpenCancelDialog(true);
    };

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        setChatBubbleContinueDone((previous) => {
            const idx = chatObjectList.length - 1;
            if (previous[idx] === true) return previous;
            const updatedArray = [...previous];
            updatedArray[idx] = true;
            return updatedArray
        });
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };


    /**
     * add sentence dialog stuff
     */
    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);

    // compute first, then pass
    const list = dialogPromptSentenceListRef.current;
    const idx = dialogPromptIndexRef.current;
    const item = list && list[idx];

    const sentenceForDialog: { text: string; id?: number } | null =
        item && item.sentence_text
            ? { text: item.sentence_text, id: item.sentence_id }
            : null;

    const handleAddSentenceClick = () => {
        setAddSentenceDialogOpen(true);
    };



    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                chatBubbleContainerRef={chatBubbleContainerRef}
                setChatBubbleDone={setChatBubbleDone}
                dialogPromptId={dialogPrompt.id}
                audioRole="prompt"
            />
            {chatObjectList.map((chatObject, index) => {
                // if ('dialog_prompt_id' in chatObject && 'sentence_id' in chatObject) {
                if (chatObject && 'dialog_prompt_id' in chatObject && 'sentence_id' in chatObject && 'sentence_text' in chatObject) {
                    // this is IDialogPromptSentence object
                    const sentenceIndex = dialogPromptSentenceListRef.current.findIndex((sentence) => sentence.id === chatObject.id);
                    let sentenceText = chatObject.sentence_text ? chatObject.sentence_text : "";
                    if (dialogPrompt.class_name == "DialogPromptConversationReadWithBlankSpeakModel") {
                        const dialogPromptRBS = dialogPrompt as DialogPromptConversationReadWithBlankSpeak;
                        sentenceText = getReadWithBlankSentenceText(dialogPromptRBS, chatObject, sentenceIndex, sentenceText);
                    } else if (dialogPrompt.class_name == "DialogPromptConversationSpeakOnlyModel") {
                        // this dialog is to speak every other sentence with no prompting.
                        // Blank the lines the student speaks, so the blank is the cue to recall
                        // and it lands before their recording. The partner's lines stay visible.
                        if (isStudentSpokenLine(sentenceIndex)) {
                            sentenceText = sentenceText.replace(/./g, '_');
                        }
                    }
                    return <Box key={index} display="inline-block">
                        <Box display="inline-flex" width="70px" /> {/* This acts as a spacer */}

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent='flex-start'
                            sx={{
                                mx: 1, py: 2,
                                display: 'inline-flex',
                                backgroundColor: sentenceIndex % 2 === 0 ? '#FFFFFF' : '#F2F2F2', // Example color condition
                            }} // Add margin to create space between Stacks
                            className='border-dark'
                        >
                            <Box sx={{ borderRight: '1.5px solid rgba(82, 99, 125, 0.8)', pr: '16px', display: 'flex', alignItems: 'center' }}>
                                {sentenceIndex % 2 === 0 && (<>
                                    <Typography sx={{
                                        fontSize: '36px',
                                        pl: '16px',
                                    }}>A</Typography>
                                </>)}
                                {sentenceIndex % 2 !== 0 && (<>
                                    <Typography sx={{
                                        fontSize: '36px',
                                        pl: '16px',
                                    }}>B</Typography>
                                </>)}
                            </Box>
                            {(dialogPrompt.class_name == "DialogPromptConversationListenSpeakModel" || dialogPrompt.class_name == "DialogPromptConversationListenWithoutCharacterModel") && (
                                <TeacherAudioActions
                                    context={buildConversationAudioReportContext(sentenceIndex)}
                                    onPlay={() => handleSpeakerClick(sentenceIndex)}
                                />
                            )}
                            {dialogPrompt.class_name != "DialogPromptConversationListenWithoutCharacterModel" && (
                                <Box
                                    className={kaiTiFontClass.customFont}
                                    data-testid="conversation-sentence-text"
                                    sx={{
                                        alignItems: 'center',
                                        display: 'inline-flex',
                                        fontSize: '36px',
                                        lineHeight: 1.1,
                                        minWidth: '1.25em',
                                        px: 2,
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    <span>{sentenceText}</span>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                    // } else if ('score' in chatObject && 'pinyin_list_spoken' in chatObject) {
                } else if (chatObject && 'score' in chatObject && 'pinyin_list_spoken' in chatObject) {
                    // this is a ISentenceScore result object 
                    const sentenceScore = chatObject as ISentenceScore;

                    return <div key={index}>
                        <ChatBubble
                            isTeacher={!dialogPrompt.is_teacher}
                            message={sentenceScore.text}
                            messageAudio={null} // sentnece score audio is already there
                            sentenceScore={sentenceScore}
                            showAvatar={showAvatar}
                            chatBubbleContainerRef={chatBubbleContainerRef}
                            setChatBubbleDone={(done) => setChatBubbleSentenceScoreDone(previous => {
                                if (previous[index] === done) return previous;
                                const updatedArray = [...previous];
                                updatedArray[index] = done;
                                return updatedArray;
                            })}
                        />
                        {chatBubbleSentenceScoreDone[index] && (<>
                            {sentenceScoreAllGood(sentenceScore) ? (
                                <ChatBubble
                                    isTeacher={true}
                                    message={dialogPromptClassNameContainsProp(dialogPrompt.class_name, "yes_response") ? (dialogPrompt as DialogPromptConversationRelated).yes_response : "ERROR1"}
                                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="yes_response"
                                    setChatBubbleDone={(done) => {
                                        setChatBubbleContinueDone((previous) => {
                                            if (previous[index] === done) return previous;
                                            const updatedArray = [...previous];
                                            updatedArray[index] = done;
                                            return updatedArray
                                        });
                                    }}
                                />
                            ) : sentenceScoreAnyFailed(sentenceScore) ? (
                                <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                    message={dialogPromptClassNameContainsProp(dialogPrompt.class_name, "no_response") ? (dialogPrompt as DialogPromptConversationRelated).no_response : "ERROR2"}
                                    messageAudio={noResponseAudio ? noResponseAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="no_response"
                                    setChatBubbleDone={() => {
                                        // set null, to skip this index
                                        setTryAgainClickedList((previous) => {
                                            if (previous[index] === null) return previous;
                                            const updatedArray = [...previous];
                                            updatedArray[index] = null;
                                            return updatedArray
                                        });
                                    }}
                                />
                            ) : (
                                <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                    message={SentenceScoreMediumResult}
                                    messageAudio={sentenceScoreMediumResultAudio ? sentenceScoreMediumResultAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="sentence_score_medium_result"
                                    setChatBubbleDone={(done) => setChatBubbleTeacherResponseDone(previous => {
                                        if (previous[index] === done) return previous;
                                        const updatedArray = [...previous];
                                        updatedArray[index] = done;
                                        return updatedArray;
                                    })}
                                />
                            )}
                        </>)}
                        {chatBubbleTeacherResponseDone[index] && (
                            <ChatBubble
                                isTeacher={true}
                                message={SentenceScoreTryAgain}
                                messageAudio={sentenceScoreTryAgainAudio ? sentenceScoreTryAgainAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                dialogPromptId={dialogPrompt.id}
                                audioRole="sentence_score_try_again"
                                setChatBubbleDone={(done) => setChatBubbleTryAgainDone(previous => {
                                    if (previous[index] === done) return previous;
                                    const updatedArray = [...previous];
                                    updatedArray[index] = done;
                                    return updatedArray;
                                })}
                            />
                        )}
                        {tryAgainClickedList[index] === true && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainYes}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                            />
                        )}
                        {tryAgainClickedList[index] === false && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainNo}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={(done) => {
                                    setChatBubbleContinueDone((previous) => {
                                        if (previous[index] === done) return previous;
                                        const updatedArray = [...previous];
                                        updatedArray[index] = done;
                                        return updatedArray
                                    });
                                }}
                            />
                        )}
                    </div>;
                } else {
                    return <div key={index}>Unknown object {JSON.stringify(chatObject)}</div>;
                }
            })}

            {/* Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
                open={addSentenceDialogOpen}
                sentence={sentenceForDialog}
                onClose={() => setAddSentenceDialogOpen(false)}
            />
        </Stack >
    );
};

export default DialogPromptConversationRelatedComponent;
